import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Confirmation, toastError, toastSuccess } from 'admination-react'
import { useApp } from '@/core/state/appStore'
import { DropCard } from '@/core/components/modal/DropCard'
import {
  GEVA_KLETT_ACCEPT,
  GEVA_KLETT_DOKUMENT,
  nameAbweichung,
  uploadGeva,
  uploadKlett,
  type GevaKlettErrorBody,
  type NameAbweichung,
  type NamePruefung,
} from '../../api/gevaKlett'
import { useGevaKlettStatus, useStatusFehlerToast, type GevaKlettStep } from './useGevaKlettStatus'
import { classifyGevaKlettFailure } from './errors'
import { GevaKlettFehlerPrompt, NameAbweichendPrompt, fehlerZeileText } from './prompts'

export function GevaKlettNachreichen({
  coachingId,
  name,
  onGespeichert,
  renderHeader,
}: {
  coachingId: string
  name: string
  onGespeichert: () => void
  renderHeader: (actions?: ReactNode) => ReactNode
}) {
  const step: GevaKlettStep = name === GEVA_KLETT_DOKUMENT.klett ? 'klett' : 'geva'
  const { t } = useTranslation('vergabeStaff')
  const { reloadDashboard } = useApp()
  const { status, loading: statusLoading, statusError, statusFehler } = useGevaKlettStatus(coachingId)
  useStatusFehlerToast(statusFehler)
  const [laedt, setLaedt] = useState(false)
  const [fehler, setFehler] = useState<GevaKlettErrorBody | null>(null)
  const [warnung, setWarnung] = useState<{ file: File; abweichung: NameAbweichung } | null>(null)

  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  async function hochladen(file: File, name: NamePruefung) {
    const ersetzt = status[step].fertig
    setLaedt(true)
    try {
      await (step === 'klett' ? uploadKlett : uploadGeva)(coachingId, file, name)
      toastSuccess(t(ersetzt ? `gevaKlett.${step}.ersetzt` : `gevaKlett.${step}.success`))
      reloadDashboard()
      if (mountedRef.current) onGespeichert()
    } catch (e) {
      const abweichung = nameAbweichung(e)
      if (abweichung && mountedRef.current) {
        setWarnung({ file, abweichung })
        return
      }
      console.error(step === 'klett' ? "Klett-Upload fehlgeschlagen:" : "GEVA-Upload fehlgeschlagen:", e)
      const failure = classifyGevaKlettFailure(e, t)
      if (failure.kind === 'prompt' && mountedRef.current) setFehler(failure.body)
      else if (failure.kind === 'prompt')
        toastError([failure.body.meldung, ...failure.body.fehler.map((f) => fehlerZeileText(f, t))].join(' '))
      else toastError(failure.message)
      if (failure.reload) reloadDashboard()
    } finally {
      if (mountedRef.current) setLaedt(false)
    }
  }

  return (
    <>
      {renderHeader()}
      {statusError ? (
        <Confirmation tone="error" title={t('gevaKlett.statusErrorTitle')}>
          {t('gevaKlett.statusErrorText')}
        </Confirmation>
      ) : (
        <DropCard
          testId="geva-klett-nachreichen"
          title={t(`gevaKlett.${step}.dropTitle`)}
          body={t(status[step].fertig ? `gevaKlett.${step}.nachreichenErsetzt` : `gevaKlett.${step}.nachreichen`)}
          accept={GEVA_KLETT_ACCEPT}
          onFile={(file) => void hochladen(file, 'pruefen')}
          loading={laedt || statusLoading}
          loadingLabel={laedt ? t(`gevaKlett.${step}.reading`) : t('gevaKlett.statusLoading')}
        />
      )}
      {fehler && <GevaKlettFehlerPrompt meldung={fehler.meldung} fehler={fehler.fehler} onClose={() => setFehler(null)} />}
      {warnung && (
        <NameAbweichendPrompt
          abweichung={warnung.abweichung}
          onSpeichern={() => {
            setWarnung(null)
            void hochladen(warnung.file, 'bestaetigt')
          }}
          onAbbrechen={() => setWarnung(null)}
        />
      )}
    </>
  )
}
